import React, { useEffect, useState } from "react";
import { ReactComponent as HomeIcon } from "assets/icons/home.svg";
import { ReactComponent as CartIcon } from "assets/icons/cart.svg";
import { ReactComponent as DollarSignPrice } from "assets/icons/dollar_sign_price.svg";
import { ReactComponent as RemoveIcon } from "assets/icons/delete_fill_2.svg";
import avatarImage from "assets/images/avatar_2.jpeg";
import useProperties from "hooks/useProperties";
import useBookmark from "hooks/useBookmark";
import AvatarList from "./AvatarList";
import RemoveWarningDialog from "components/Dialog/RemoveWarningDialog";
import { useOrganiseList } from "context/OrganiseListContext";
import useSaveSearch from "hooks/useSaveSearch";
import OrganizerLog from "./OrganizerLog";
import useAuth from "hooks/useAuth";

const StatBox = ({ number, label }) => (
  <div className="border h-[155px] border-[#EEEEEE] rounded-[10px] flex flex-col items-center justify-center">
    <div className="w-[52px] h-[52px] rounded-full bg-[#E8F6FF] grid place-items-center text-primary font-bold text-[22px]">
      {number}
    </div>
    <h1 className="mt-[10px] font-semibold text-center text-black leading-[24px] text-[20px] px-1">
      {label}
    </h1>
  </div>
);

const ActivityBox = ({ Icon, message, value }) => (
  <div className="bg-[#F2F2F2] h-[32px] rounded-[7px] flex items-center space-x-[7px] px-[9px]">
    <div className="w-[26px] h-[26px] bg-[#E8F6FF] rounded-full grid place-items-center">
      <Icon />
    </div>
    <p className="text-[10px] font-medium leading-[1.1]">{message}</p>
  </div>
);

const OrganizeInfoSection = () => {
  const {
    bookmarkedProperties,
    getRecentBookmarkPropertyOverview,
    recentBookmarkPropertyOverview,
    isFetchingRecentBookmarkOverviewProperty,
  } = useBookmark();
  const { externalProperties } = useProperties();
  const [isOpenOrganiseListRemovePopup, setIsOpenOrganiseListRemovePopup] =
    useState(false);
  const { selectedOrganiseList, removeOrganiseList, loading, isFetchingList } =
    useOrganiseList();
  const { saveSearches, fetchSaveSearches } = useSaveSearch();
  const { user } = useAuth();

  // Fetch save searches
  useEffect(() => {
    fetchSaveSearches();
  }, [user]);

  useEffect(() => {
    if (!isFetchingList && selectedOrganiseList && user) {
      getRecentBookmarkPropertyOverview();
    }
  }, [user, isFetchingList, selectedOrganiseList]);

  // Remove organise list
  const handleRemoveOrganiseList = async () => {
    await removeOrganiseList(selectedOrganiseList?._id);
    setIsOpenOrganiseListRemovePopup(false);
  };

  // Total save properties calculation
  const calculateTotalSavedProperties = () => {
    const bookmarkedCount =
      bookmarkedProperties?.pagination?.totalDocuments || 0;
    const externalCount = externalProperties?.pagination?.totalDocuments || 0;
    return bookmarkedCount + externalCount;
  };

  const propertyOverviewData = [
    {
      Icon: HomeIcon,
      message: (
        <p>
          Hai aggiunto{" "}
          <span className="text-primary text-[14px] font-semibold">
            {recentBookmarkPropertyOverview?.addedToBookmarkListCount || 0}{" "}
          </span>
          proprietà.
        </p>
      ),
    },
    {
      Icon: CartIcon,
      message: (
        <p>
          <span className="text-primary text-[14px] font-semibold">
            {recentBookmarkPropertyOverview?.notRefetchedCount || 0}{" "}
          </span>
          Proprietà sono state vendute!
        </p>
      ),
    },
    {
      Icon: DollarSignPrice,
      message: (
        <p>
          <span className="text-primary text-[14px] font-semibold">
            {recentBookmarkPropertyOverview?.priceChangedCount || 0}{" "}
          </span>{" "}
          Proprietà hanno subito una variazione di prezzo.
        </p>
      ),
    },
  ];

  return (
    <>
      <div className="xl:h-[220px] max-w-[1550px] mx-auto w-full rounded-[10px] bg-white pt-4 lg:pt-[35px] pb-4 lg:pb-[30px] px-4 lg:px-[25px] grid gap-y-4 xl:gap-y-0  xl:grid-cols-8 gap-x-[13px]">
        <div className="col-span-4 grid grid-cols-2 md:grid-cols-4 gap-[13px]">
          <StatBox
            number={calculateTotalSavedProperties()}
            label="Proprietà Salvate"
          />
          <StatBox
            number={saveSearches?.pagination?.totalDocuments || 0}
            label="Ricerche Salvate"
          />
          <OrganizerLog />
        </div>
        <AvatarList />

        <div className="border col-span-3 md:col-span-2   border-[#EEEEEE] rounded-[10px] py-3">
          <h1 className="text-[12px] px-4 font-semibold leading-[24px] text-black">
            Negli ultimi 7 giorni
          </h1>
          <div className="grid gap-y-[5px] px-2 sm:px-4">
            {!isFetchingRecentBookmarkOverviewProperty &&
              propertyOverviewData.map(({ Icon, message, value }, index) => (
                <ActivityBox
                  key={index}
                  Icon={Icon}
                  message={message}
                  value={value}
                />
              ))}
          </div>
        </div>
        <div
          onClick={() => setIsOpenOrganiseListRemovePopup(true)}
          className="border border-[#EEEEEE] flex-col cursor-pointer rounded-[10px] bg-[#FF6D59] flex items-center justify-center"
        >
          <RemoveIcon className="text-white w-10 h-10 sm:w-[54px] sm:h-[54px] fill-white" />

          <h2 className="text-[15px] text-center font-semibold leading-[24px] text-white mt-[11px]">
            Elimina Lista
          </h2>
        </div>
      </div>
      <RemoveWarningDialog
        isOpen={isOpenOrganiseListRemovePopup}
        onClick={handleRemoveOrganiseList}
        isClose={() => setIsOpenOrganiseListRemovePopup(false)}
        warningText={`Vuoi eliminare la lista ${selectedOrganiseList?.name}?`}
        loading={loading}
      />
    </>
  );
};

export default OrganizeInfoSection;
